import { z } from "zod";

/**
 * First-run admin creation. Public but gated on the one-time setup token
 * (printed to `docker logs` + written to the cm-state volume) and only
 * honoured while the users table is empty. See POST /api/system/setup/admin.
 */
export const SetupAdminSchema = z.object({
	username: z.string().min(1, "Username is required").max(120),
	password: z
		.string()
		.min(8, "Password must be at least 8 characters")
		.max(512),
	setupToken: z.string().min(1, "Setup token is required").max(256),
});
export type SetupAdminInput = z.infer<typeof SetupAdminSchema>;

/**
 * Optional Authenticated Origin Pulls (mTLS) config, only meaningful for
 * Cloudflare + Full. `zoneCaPem`, when present, is the zone-level origin-pull
 * CA to verify against (written to the cm-state volume); omitted uses the
 * bundled global Cloudflare origin-pull CA.
 */
const MtlsSchema = z.object({
	enabled: z.boolean(),
	zoneCaPem: z.string().max(1_000_000).optional(),
});
export type MtlsInput = z.infer<typeof MtlsSchema>;

/**
 * TLS mode selection (edge-hardening model). Primary `mode`:
 *   - `cloudflare` — TLS terminated at the Cloudflare edge. Carries a
 *       Flexible/Full sub-choice (`cfMode`):
 *         · flexible — origin serves plain :80, locked to Cloudflare IPs.
 *         · full     — origin serves :443 with an origin cert whose source is
 *                      `fullCertSource` ("self-signed" | "origin-ca"). Origin
 *                      CA requires a one-shot `originCaToken` (never persisted).
 *                      Optional `mtls` (Authenticated Origin Pulls).
 *       `originLock` (default true) toggles the Cloudflare-IP origin lock.
 *   - `byo` — uploads a PEM cert + key written to the cm-state volume.
 *   - `acme` — automatic HTTPS via lua-resty-acme (Let's Encrypt HTTP-01). An
 *       optional contact email receives expiry notices.
 *
 * Modelled as a union (the discriminator differs per branch: mode, then cfMode,
 * then fullCertSource) so each branch carries exactly the fields it needs.
 * TlsUploadSchema is the source of truth; the Elysia body guard is coarse.
 */
const CloudflareFlexibleSchema = z.object({
	mode: z.literal("cloudflare"),
	cfMode: z.literal("flexible"),
	originLock: z.boolean().optional(),
});

const CloudflareFullSelfSignedSchema = z.object({
	mode: z.literal("cloudflare"),
	cfMode: z.literal("full"),
	fullCertSource: z.literal("self-signed"),
	originLock: z.boolean().optional(),
	mtls: MtlsSchema.optional(),
});

const CloudflareFullOriginCaSchema = z.object({
	mode: z.literal("cloudflare"),
	cfMode: z.literal("full"),
	fullCertSource: z.literal("origin-ca"),
	// One-shot Cloudflare API token used to issue the Origin CA cert. Never
	// persisted — consumed by the origin-ca-issue Operation and discarded.
	originCaToken: z
		.string()
		.max(4096)
		.refine(
			(v) => v.trim().length > 0,
			"originCaToken is required for Origin CA",
		),
	originLock: z.boolean().optional(),
	mtls: MtlsSchema.optional(),
});

const ByoSchema = z.object({
	mode: z.literal("byo"),
	cert: z
		.string()
		.max(1_000_000)
		.refine((v) => v.trim().length > 0, "cert is required for byo mode"),
	key: z
		.string()
		.max(1_000_000)
		.refine((v) => v.trim().length > 0, "key is required for byo mode"),
});

const AcmeSchema = z.object({
	mode: z.literal("acme"),
	// Optional Let's Encrypt account contact (expiry notices). Empty string
	// is coerced to undefined so a blank field doesn't fail email parsing.
	email: z
		.string()
		.trim()
		.email("email must be a valid email address")
		.optional()
		.or(z.literal("").transform(() => undefined)),
});

export const TlsUploadSchema = z.union([
	CloudflareFlexibleSchema,
	CloudflareFullSelfSignedSchema,
	CloudflareFullOriginCaSchema,
	ByoSchema,
	AcmeSchema,
]);
export type TlsUploadInput = z.infer<typeof TlsUploadSchema>;
